"use client";

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AssistantOnboardingHeader from "../../components/AssistantOnboardingHeader";
import providersService from "../../api/services/providersService";
import useEditFlow from "../../hooks/useEditFlow";
import { getStoredUserIdentifier } from "../../utils/authSession";
import { useToast } from "../../hooks/useToast";

const languages = [
  "English", "Hausa", "Yoruba", "Igbo", "French", "Chinese",
  "Zulu", "Akan", "Ebira", "Fulani", "Spanish", "Mandarin",
  "Ijebu", "Arabic", "German",
];

const specializations = [
  "Historical", "Food", "Adventures", "Cultural", "Religious",
  "Eco-Tourism", "Photography", "Sports",
];

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.values(value)
      .flat()
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean);
  }
  return [];
};

const formatYears = (value) => {
  if (value == null) return "";
  if (typeof value === "number") return String(value);
  return String(value).trim();
};

// ✅ Robust unwrapping for different backend shapes
const unwrapExpertise = (res) => {
  if (!res) return {};
  return (
    res?.data?.data?.profile ||
    res?.data?.data?.expertise ||
    res?.data?.data ||
    res?.data?.profile ||
    res?.data?.expertise ||
    res?.data ||
    res
  );
};




export default function AssistantExperience({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const fromProfile = Boolean(location.state?.fromProfile);
  const isEditingProfile = useEditFlow(fromProfile);
  const toast = useToast();

  const userIdentifier = currentUser?.id || currentUser?.email || getStoredUserIdentifier() || "guest";
  const storageKey = useMemo(
    () => `assistantExperienceDraft_${userIdentifier}`,
    [userIdentifier]
  );

  const [years, setYears] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  const toggle = (value, listSetter) => {
    listSetter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // 1) Draft hydration (ONLY when NOT editing)
  useEffect(() => {
    if (isEditingProfile) {
      setHasDraft(false);
      setIsHydrated(true);
      return;
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setYears("");
      setSelectedLanguages([]);
      setSelectedSpecializations([]);
      setHasDraft(false);
      setIsHydrated(true);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      setYears(parsed.years || "");
      setSelectedLanguages(Array.isArray(parsed.languages) ? parsed.languages : []);
      setSelectedSpecializations(
        Array.isArray(parsed.specializations) ? parsed.specializations : []
      );
      setHasDraft(true);
    } catch (e) {
      setHasDraft(false);
    } finally {
      setIsHydrated(true);
    }
  }, [isEditingProfile, storageKey]);

  // 2) Fetch expertise from API (in edit mode ALWAYS, in normal mode ONLY if no draft)
  useEffect(() => {
    if (!isHydrated) return;
    if (!isEditingProfile && hasDraft) return;

    let active = true;

    const fetchExpertise = async () => {
      setIsLoading(true);
      try {
        const response = await providersService.getExpertise();
        const data = unwrapExpertise(response);
        const expertise = data?.profile ?? data;

        if (!active || !data) return;

const expYears =
  expertise.experienceYears ??
  expertise.experience_years ??
  expertise.years;

const normalizedLanguages = normalizeList(
  expertise.languages ?? expertise.language ?? expertise.languageSpoken
);

const normalizedSpecializations = normalizeList(
  expertise.specializations ?? expertise.specialization ?? expertise.areas ?? expertise.area
);

if (expYears != null) setYears(formatYears(expYears));
if (normalizedLanguages.length) setSelectedLanguages(normalizedLanguages);
if (normalizedSpecializations.length) setSelectedSpecializations(normalizedSpecializations);

      } catch (e) {
        // ignore load errors
      } finally {
        if (active) setIsLoading(false);
      }
    };

    fetchExpertise();
    return () => {
      active = false;
    };
  }, [isHydrated, hasDraft, isEditingProfile, fromProfile]);

  // 3) Save draft (ONLY when NOT editing)
  useEffect(() => {
    if (!isHydrated || isEditingProfile) return;
    const draft = {
      years,
      languages: selectedLanguages,
      specializations: selectedSpecializations,
    };
    localStorage.setItem(storageKey, JSON.stringify(draft));
  }, [years, selectedLanguages, selectedSpecializations, isHydrated, storageKey, isEditingProfile]);

  const parsedYears = Number(String(years).replace(/[^\d.]/g, ""));
  const isFormComplete =
    Number.isFinite(parsedYears) &&
    parsedYears > 0 &&
    selectedLanguages.length > 0 &&
    selectedSpecializations.length > 0;

  const handleContinue = async () => {
    if (!Number.isFinite(parsedYears) || parsedYears <= 0) {
      toast.error("Please enter your years of experience.");
      return;
    }
    if (!selectedLanguages.length) {
      toast.error("Please select at least one language.");
      return;
    }
    if (!selectedSpecializations.length) {
      toast.error("Please select at least one specialization.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        experienceYears: parsedYears,
        languages: selectedLanguages,
        specializations: selectedSpecializations,
      };

      if (isEditingProfile) {
        await providersService.updateExpertise(payload);
      } else {
        await providersService.submitExpertise(payload);
      }

      localStorage.removeItem(storageKey);

      const navOptions = isEditingProfile ? { state: { fromProfile: true } } : undefined;
      navigate("/assistant/documents", navOptions);
    } catch (err) {
      toast.apiError(err, "Unable to save experience details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <AssistantOnboardingHeader />

      <main className="mx-auto flex max-w-4xl flex-col px-4 pb-16 pt-10">
        <div className="text-center space-y-6">
          <h1 className="text-3xl font-semibold text-gray-900">Experience</h1>
          <div className="mx-auto h-2 w-full max-w-2xl overflow-hidden rounded-full bg-[#d3d6dd]">
            <div className="h-full w-[40%] rounded-full bg-[#7a0051]" />
          </div>
          <p className="text-md font-medium text-gray-600">
            This information will help travellers find you. Be as detailed as possible.
          </p>
        </div>

        <div className="mt-10 space-y-8">
          <div className="space-y-2">
            <label className="font-medium text-gray-800">
              Years of experience <span className="font-bold text-red-600">*</span>
            </label>
            <input
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              placeholder="e.g 5 years"
              min="1"
              className="w-full rounded-lg border border-[#d8dbe0] bg-white px-4 py-3 text-base font-medium text-gray-800 placeholder:text-gray-400 focus:border-[#7a0051] focus:outline-none focus:ring-1 focus:ring-[#7a0051]/30"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="font-medium text-gray-800">
                Languages <span className="font-bold text-red-600">*</span>
              </label>
              <span className="text-sm font-medium text-gray-500">
                {selectedLanguages.length} Selected
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {languages.map((lang) => {
                const active = selectedLanguages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggle(lang, setSelectedLanguages)}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                      active
                        ? "border-[#61013B] bg-[#61013B] text-white"
                        : "border-[#CEB0C2] bg-white text-gray-800"
                    }`}
                    disabled={isLoading}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="font-medium text-gray-800">
                Areas of Specialisation <span className="font-bold text-red-600">*</span>
              </label>
              <span className="text-sm font-medium text-gray-500">
                {selectedSpecializations.length} Selected
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {specializations.map((item) => {
                const active = selectedSpecializations.includes(item);
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggle(item, setSelectedSpecializations)}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                      active
                        ? "border-[#61013B] bg-[#61013B] text-white"
                        : "border-[#CEB0C2] bg-white text-gray-800"
                    }`}
                    disabled={isLoading}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <button
              type="button"
              onClick={handleContinue}
              className={`w-full rounded-lg px-4 py-3 text-center text-base font-semibold text-white shadow-sm transition ${
                isFormComplete
                  ? "bg-[#61013B] hover:bg-[#4f0130]"
                  : "bg-[#caa8b8] hover:bg-[#b792a3]"
              }`}
              disabled={!isFormComplete || isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Continue"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full rounded-lg border border-[#7a0051] px-4 py-3 text-center text-base font-semibold text-[#7a0051] transition hover:bg-[#f7eef4]"
            >
              Back
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
